/**
 * Boot prefetch: warm menu cache when subscription dates are ready.
 * Shared gate with foodMenu — only one domain batch runs per customer.
 */
import { useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import type { DayConfig } from '../subscription/types';
import type { MenuBuilder } from './types';
import { runAfterInteractions } from '../../../shared/utils/defer';
import { useSubscriptionContext } from '../subscription/hooks';
import { fetchMenu } from './api';
import { menuKeys } from './keys';
import { hasPastDateMenuCached, menuCacheTimingForDate } from './cache';
import { useMenuStore } from './store';
import {
  BOOT_MENU_FIRST_BATCH_SIZE,
  tryClaimBootMenuPrefetch,
  markBootMenuPrefetchComplete,
  resetBootMenuPrefetchGate,
  runWhenSubscriptionFingerprintStable,
} from '../menuPrefetch';

export function resetMenuPrefetchGate(): void {
  resetBootMenuPrefetchGate();
}

export function syncMenuCacheToStore(
  query_client: ReturnType<typeof useQueryClient>,
  customer_id: number,
  date: string,
  day_config_map: Record<string, DayConfig>,
): void {
  if (useMenuStore.getState().isDateDivergentFromServer(date)) {
    return;
  }
  const key = menuKeys.day(customer_id, date, false);
  const cached = query_client.getQueryData(key) as { menu_builders?: MenuBuilder[] } | undefined;
  if (!cached?.menu_builders) {
    return;
  }
  const config = day_config_map[date];
  const day_config: DayConfig | null = config ? { ...config, isDoubleBox: config.isDoubleBox ?? false } : null;
  useMenuStore.getState().setMenuData(cached.menu_builders, date, day_config, false);
}

export function useMenuPrefetchOnSubscriptionReady() {
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);
  const { availableDates, isLoading, dayConfigMap, activeSubscriptions } = useSubscriptionContext();
  const did_prefetch_ref = useRef(false);

  const subscription_fingerprint = useMemo(() => {
    const active_id = activeSubscriptions[0]?.id ?? 0;
    const double_count = Object.values(dayConfigMap ?? {}).filter(c => c?.isDoubleBox).length;
    return `${active_id}|${availableDates.length}|${double_count}|${availableDates[0] ?? ''}|${availableDates[availableDates.length - 1] ?? ''}`;
  }, [activeSubscriptions, availableDates, dayConfigMap]);

  useEffect(() => {
    if (typeof customer_id !== 'number' || isLoading || availableDates.length === 0) {
      return;
    }
    if (did_prefetch_ref.current) {
      return;
    }

    const cancel_stable = runWhenSubscriptionFingerprintStable(subscription_fingerprint, () => {
      if (did_prefetch_ref.current) {
        return;
      }
      if (!tryClaimBootMenuPrefetch(customer_id)) {
        did_prefetch_ref.current = true;
        return;
      }
      did_prefetch_ref.current = true;

      moment.locale('en');
      const today_ymd = moment().format('YYYY-MM-DD');
      const sorted = [...availableDates].map(d => ensureAsciiDateString(d) || d).sort();
      const has_today = sorted.includes(today_ymd);
      const preferred_date = has_today ? today_ymd : sorted[0];
      const rest = sorted.filter(d => d !== today_ymd);
      const to_prefetch = (has_today ? [today_ymd, ...rest] : sorted).slice(
        0,
        Math.min(BOOT_MENU_FIRST_BATCH_SIZE, sorted.length),
      );
      const day_config_map = dayConfigMap ?? {};
      const prefetch_today_double_box = has_today && day_config_map[today_ymd]?.isDoubleBox === true;

      runAfterInteractions(() => {
        const current_selected = useMenuStore.getState().selectedDate;
        if (preferred_date && !current_selected) {
          useMenuStore.getState().setSelectedDate(preferred_date);
        }
        const prefetch_one = (date: string, is_double_box: boolean) => {
          const key = menuKeys.day(customer_id, date, is_double_box);
          const existing = query_client.getQueryData(key);
          if (existing != null) {
            if (!is_double_box) {
              syncMenuCacheToStore(query_client, customer_id, date, day_config_map);
            }
            return Promise.resolve();
          }
          if (hasPastDateMenuCached(query_client, customer_id, date, is_double_box)) {
            return Promise.resolve();
          }
          const { staleTime, gcTime } = menuCacheTimingForDate(date);
          return query_client
            .prefetchQuery({
              queryKey: key,
              queryFn: () => fetchMenu(customer_id, date, is_double_box),
              staleTime,
              gcTime,
            })
            .then(() => {
              if (!is_double_box) {
                syncMenuCacheToStore(query_client, customer_id, date, day_config_map);
              }
            });
        };

        const run_batch = (): Promise<unknown> => {
          if (prefetch_today_double_box) {
            return prefetch_one(today_ymd, true).then(() =>
              Promise.all(to_prefetch.map(date => prefetch_one(date, false))),
            );
          }
          return Promise.all(to_prefetch.map(date => prefetch_one(date, false)));
        };

        void run_batch().finally(() => {
          markBootMenuPrefetchComplete();
        });
      });
    });

    return cancel_stable;
  }, [customer_id, isLoading, availableDates, dayConfigMap, query_client, subscription_fingerprint]);
}
